import { londonDateParts } from '@utils/formatters';

export const MIN_AUTO_ROLL_TENOR_WEEKS = 4;
export const MAX_AUTO_ROLL_TENOR_WEEKS = 25;

// 25, not 26: the range tops out at 25 weeks, so the six-month bill is offered at its nearest term.
export const STANDARD_TENOR_WEEKS = [4, 13, 25].filter(
  (weeks) => weeks >= MIN_AUTO_ROLL_TENOR_WEEKS && weeks <= MAX_AUTO_ROLL_TENOR_WEEKS,
);

export const DEFAULT_AUTO_ROLL_TENOR_WEEKS = 13;

const DAY_MS = 24 * 60 * 60 * 1000;

export const clampTenorWeeks = (weeks: number): number => {
  if (!Number.isFinite(weeks)) return DEFAULT_AUTO_ROLL_TENOR_WEEKS;
  return Math.min(
    MAX_AUTO_ROLL_TENOR_WEEKS,
    Math.max(MIN_AUTO_ROLL_TENOR_WEEKS, Math.round(weeks)),
  );
};

// The listing mapper substitutes `new Date()` for a missing date, so a maturity that is not in the
// future means "unknown", not "shorter than a week".
export const suggestedAutoRollTenorWeeks = (
  maturityDate: Date | null | undefined,
  now: Date = new Date(),
): number => {
  if (!maturityDate || !Number.isFinite(maturityDate.getTime())) {
    return DEFAULT_AUTO_ROLL_TENOR_WEEKS;
  }

  const weeks = weeksUntil(maturityDate, now);
  return weeks === 0 ? DEFAULT_AUTO_ROLL_TENOR_WEEKS : clampTenorWeeks(weeks);
};

export const formatTenorWeeks = (weeks: number): string =>
  weeks === 1 ? '1 week' : `${weeks} weeks`;

export const formatTenorBills = (weeks: number): string => `${weeks}-week bills`;

// Spoken aloud, 8, 11 and 18 start with a vowel sound: "an 8-week bill".
export const tenorArticle = (weeks: number): 'a' | 'an' =>
  weeks === 11 || weeks === 18 || String(weeks).startsWith('8') ? 'an' : 'a';

export const projectedRollMaturity = (maturityDate: Date, weeks: number): Date => {
  const { day, month, year } = londonDateParts(maturityDate);
  // Noon UTC falls on the same London calendar day in both GMT and BST.
  const target = new Date(Date.UTC(year, month, day + weeks * 7, 12));
  const daysSinceMonday = (target.getUTCDay() + 6) % 7;
  target.setUTCDate(target.getUTCDate() - daysSinceMonday);
  return target;
};

// Rounded up, so a bill maturing in 2 days still reads "1 week", never "0 weeks".
export const weeksUntil = (date: Date, now: Date = new Date()): number =>
  Math.max(0, Math.ceil((date.getTime() - now.getTime()) / (7 * DAY_MS)));
